import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

const safetyRelated: RL[] = [
  {
    title: "Can SAINT® Make Depression Worse?",
    href: "/learn/can-saint-make-depression-worse",
    description:
      "What the research says about worsening symptoms and what to watch for",
  },
  {
    title: "What Does SAINT® TMS Feel Like?",
    href: "/learn/what-does-saint-tms-feel-like",
    description: "A first-person account of the sensation during treatment",
  },
  {
    title: "What Happens After SAINT® TMS?",
    href: "/learn/what-happens-after-saint-tms",
    description: "Recovery, follow-up, and what to expect after the week",
  },
  {
    title: "Am I a Candidate for SAINT®?",
    href: "/am-i-a-candidate",
    description: "Check your eligibility for SAINT® TMS treatment",
  },
  {
    title: "SAINT® Accelerated TMS",
    href: "/saint",
    description: "The 5-day, fMRI-guided protocol offered at our clinic",
  },
  {
    title: "What Is Treatment-Resistant Depression?",
    href: "/learn/what-is-treatment-resistant-depression",
    description: "Definition, causes, and treatment options",
  },
];

const faqs: LearnFAQ[] = [
  {
    question: "Is SAINT TMS safe?",
    answer:
      "SAINT® is FDA-cleared for major depressive disorder in adults whose depression hasn't improved enough on prior antidepressant medication. In the 2022 randomized trial, the most common side effects were headache and scalp discomfort, which were mild to moderate and temporary (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. The most serious risk — seizure — is rare. A psychiatric evaluation determines whether SAINT® is appropriate for you.",
  },
  {
    question: "Can TMS cause brain damage?",
    answer:
      "No evidence shows that TMS damages brain tissue. TMS uses magnetic pulses to stimulate neurons — it does not create lesions, destroy tissue, or cause structural injury. Decades of TMS research, including brain imaging studies, have not identified brain damage as a risk. SAINT® uses the same magnetic stimulation technology as standard TMS, delivered in an accelerated schedule.",
  },
  {
    question: "What are the long-term effects of TMS?",
    answer:
      "Long-term safety data for accelerated protocols like SAINT® are still accumulating. Standard TMS has been studied for over 20 years, and no cumulative toxicity or delayed injury has been identified. The most common long-term consideration is whether symptom relief is sustained — some people benefit from additional treatment courses over time. Your psychiatrist discusses follow-up and monitoring based on your individual response.",
  },
  {
    question: "Who should not get TMS?",
    answer:
      "People with non-removable magnetic-sensitive metal in or near the head, certain implanted electronic devices, or increased intracranial pressure should not receive SAINT®. People who cannot safely have an MRI are not candidates. A history of seizures requires careful evaluation. SAINT® is not FDA-cleared for bipolar depression, PTSD, anxiety disorders alone, OCD, or anyone under 18. NWCOA treats adults 22 and older.",
  },
  {
    question: "Does SAINT® cause memory problems like ECT?",
    answer:
      "No. SAINT® does not cause the memory problems that can be associated with ECT. SAINT® does not use anesthesia, does not induce a seizure, and does not require recovery time. Patients remain awake and alert throughout treatment. This is one of the key differences between SAINT® and ECT.",
  },
  {
    question: "What happens if I feel worse during treatment?",
    answer:
      "Tell the treatment team right away. The team monitors you during each session and can adjust intensity, pause treatment, or recommend a psychiatric review. As with any depression treatment, there is a risk of worsening symptoms or suicidal thoughts. If you are in crisis, call or text 988. Your care team takes any change in mood seriously.",
  },
];

export const pageIsSaintTmsSafe = makeArticle({
  slug: "is-saint-tms-safe",
  title:
    "SAINT® TMS Side Effects, Long-Term Effects & Safety Risks | Neuro Wellness",
  description:
    "Concise answer to SAINT® TMS side effects (headache, scalp discomfort) followed by rare risks, seizure screening, contraindications, and long-term effects.",
  canonical: "/learn/is-saint-tms-safe",
  h1: "SAINT® TMS Side Effects, Long-Term Effects & Safety Risks",
  breadcrumbLabel: "SAINT® TMS Side Effects & Safety",
  shortAnswer:
    "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is FDA-cleared for major depressive disorder in adults whose depression hasn't improved enough on prior antidepressant medication. The most common side effects are headache and scalp discomfort, which are usually mild and temporary. The most serious risk — seizure — is rare. No evidence shows TMS damages the brain. People with certain metal implants or implanted devices should not receive SAINT®. If you are in crisis, call or text 988.",
  keyTakeaways: [
    "SAINT® is FDA-cleared for MDD in adults after prior antidepressant medication.",
    "Headache and scalp discomfort are the most common side effects — usually mild and temporary.",
    "Seizure is the most serious risk but is rare with proper screening.",
    "No evidence shows TMS causes brain damage or memory problems.",
    "People with magnetic-sensitive metal in the head or certain implants should not receive SAINT®.",
    "If you are in crisis, call or text 988.",
  ],
  sections: [
    {
      heading: "SAINT\u00ae TMS side effects: quick answer",
      paragraphs: [
        "The most common side effects of SAINT\u00ae TMS are headache and scalp discomfort at the stimulation site, which are usually mild to moderate and tend to ease over the first day or two (Cole et al., Am J Psychiatry 2022;179(2):132\u2013141). Facial muscle twitching during pulses is common and harmless. Some people report fatigue or anxiety during the treatment week. The most serious risk \u2014 seizure \u2014 is rare with proper screening. SAINT\u00ae does not cause the memory problems associated with ECT, and there is no anesthesia or sedation.",
        "People with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or who cannot safely have an MRI should not receive SAINT\u00ae. If you are in crisis or having thoughts of suicide, call or text 988. The sections below go deeper into each risk, the long-term evidence, and the contraindications.",
      ],
    },
    {
      heading: "Quick Answer: Is SAINT\u00ae TMS Safe?",
      paragraphs: [
        "SAINT\u00ae is FDA-cleared for the treatment of major depressive disorder (MDD) in adults whose depression has not improved enough on prior antidepressant medication. The most commonly reported side effects in the clinical trial were headache and scalp discomfort, which were mild to moderate and tended to decrease over the first day or two of treatment (Cole et al., Am J Psychiatry 2022;179(2):132\u2013141). Individual results may vary.",
        "The most serious risk associated with TMS is seizure, which is rare. No evidence from decades of TMS research shows that the magnetic pulses used in TMS damage brain tissue. SAINT\u00ae does not cause the memory problems, sedation, or anesthesia-related risks associated with some other brain-based treatments. People with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure should not receive SAINT\u00ae.",
        "If you are in crisis or having thoughts of suicide, call or text 988. The 988 Suicide and Crisis Lifeline is available 24/7.",
      ],
    },
    {
      heading: "Common Side Effects of SAINT® TMS",
      paragraphs: [
        "The most commonly reported side effects of SAINT® and TMS generally are headache, scalp discomfort or pain at the stimulation site, and facial muscle twitching during pulses. In the THREE-D trial — a large randomized study of standard TMS — headache was reported by about two-thirds of participants (Blumberger et al., Lancet 2018;391(10131):1683–1692). In the 2022 SAINT® randomized trial, headache and scalp discomfort were the most frequently reported adverse events, and they were generally mild to moderate in severity (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary.",
        "These effects tend to decrease over the first day or two of treatment as the scalp adjusts to stimulation. Over-the-counter pain relievers such as acetaminophen or ibuprofen are often sufficient for headaches. The treatment team can adjust coil position or reduce stimulation intensity if discomfort persists.",
        "Facial muscle twitching during pulses is common and harmless — it happens because the magnetic field stimulates the nerves that control facial muscles near the treatment site. It stops when stimulation stops. Most people find it surprising at first but not painful.",
      ],
    },
    {
      heading:
        "Rare but Serious Risks: Seizure, Mania, and Worsening Depression",
      paragraphs: [
        "Seizure is the most serious risk associated with TMS. With standard TMS parameters and proper screening, the risk of seizure is estimated at less than 0.1% of sessions [VERIFY — exact rate for accelerated protocols]. A history of seizures requires careful evaluation and may rule out treatment. The screening process is designed to identify people at elevated seizure risk before treatment begins.",
        "TMS can also, rarely, trigger manic symptoms in people with bipolar disorder. This is why screening for bipolar disorder is part of the evaluation before SAINT®. SAINT® is not FDA-cleared for bipolar depression.",
        "As with any depression treatment, there is a risk of worsening depression or suicidal thoughts. The 2022 SAINT® trial monitored participants for these risks, and the treatment team checks in with you throughout the week. If you notice your mood getting worse, tell the team immediately. If you are in crisis, call or text 988.",
        "Because accelerated protocols deliver many more pulses per day and have been studied in far fewer people than conventional TMS, rare risks are less well characterized. This is a limitation of the current evidence that your psychiatrist can discuss with you.",
      ],
    },
    {
      heading: "Long-Term Effects: What the Evidence Does and Doesn't Show",
      paragraphs: [
        "Long-term safety data for accelerated TMS protocols like SAINT® are still accumulating. The 2022 randomized trial tracked outcomes over several weeks of follow-up (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary, and some people may benefit from additional treatment courses over time.",
        "Standard TMS has been studied for over 20 years, and no cumulative toxicity, delayed injury, or progressive harm has been identified in the research literature. The magnetic pulses used in TMS do not accumulate in the body or cause cumulative damage with repeated exposure.",
        "What the evidence does not yet show is how SAINT®-specific outcomes hold up over months and years in large, diverse populations. The published SAINT® data come from relatively small trials. Your psychiatrist can discuss what follow-up and monitoring look like after the treatment week, and what the current evidence does and does not cover for your individual situation.",
        "If you have questions about long-term effects, [[talk to your psychiatrist|/contactus]] or [[check if SAINT® is a fit|/am-i-a-candidate-screener]].",
      ],
    },
    {
      heading: "Does TMS Damage the Brain? Myth vs. Data",
      paragraphs: [
        "No. There is no evidence that TMS damages brain tissue. TMS uses focused magnetic pulses to stimulate neurons in a specific brain region — it does not create lesions, destroy tissue, or cause structural injury. Brain imaging studies of TMS patients have not identified brain damage as a risk.",
        "This is a common concern because TMS involves direct stimulation of the brain. But the magnetic field in TMS is the same type used in MRI scanners — it is non-ionizing, meaning it does not carry the radiation risks associated with X-rays or CT scans. The pulses temporarily increase neural activity in the target region; they do not permanently alter brain structure.",
        "SAINT® uses the same magnetic stimulation technology as standard TMS, delivered in an accelerated schedule (10 sessions per day over 5 days). The difference is the schedule and the personalized targeting — not the fundamental safety profile of the magnetic pulses themselves.",
        "Some people worry that the accelerated schedule — 50 sessions in one week — could overwhelm the brain. The 2022 trial did not show evidence of harm from the accelerated schedule, though the trial was small and long-term data are still accumulating (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. This is something to discuss with your psychiatrist during your consultation.",
      ],
    },
    {
      heading: "Who Should Not Have SAINT®: Contraindications",
      paragraphs: [
        "People with non-removable magnetic-sensitive metal in or near the head should not receive SAINT®. This includes cochlear implants, aneurysm clips, certain stents, and other ferromagnetic implants. The magnetic field could move or heat the metal, causing injury. Implanted electronic stimulators — such as deep brain stimulation devices — are also a contraindication.",
        "People who cannot safely have an MRI are not candidates for SAINT®, because every SAINT\u00ae patient requires a brain scan before treatment. [[Does SAINT® require an MRI?|/learn/does-saint-tms-require-an-mri]]",
        "SAINT® is not FDA-cleared for bipolar depression, PTSD, anxiety disorders alone, OCD, concussion, or any condition other than major depressive disorder in adults. NWCOA treats adults 22 and older. SAINT® is not for anyone under 18.",
        "A thorough psychiatric evaluation at NWCOA reviews your medical history, medications, and any implants before recommending SAINT®. [[Check your eligibility|/am-i-a-candidate]] or [[learn about treatment-resistant depression|/learn/what-is-treatment-resistant-depression]].",
      ],
    },
    {
      heading: "How the Clinic Monitors You During the SAINT® Week",
      paragraphs: [
        "During the five-day SAINT® week, the treatment team monitors you at each session. You sit awake in a chair while a coil on your scalp delivers magnetic pulses. The team checks in on how you're feeling, adjusts intensity if needed, and tracks your mood throughout the week.",
        "Unlike SPRAVATO® (esketamine), SAINT® does not require a post-session monitoring period — you can generally drive yourself to and from the clinic and return to your day after each session. There is no sedation and no anesthesia.",
        "The team also watches for any signs of worsening mood or new symptoms. If you feel worse, tell the team right away. They can pause treatment, adjust the plan, or bring in a psychiatric review. As with any depression treatment, there is a risk of worsening depression or suicidal thoughts, and the team takes any change seriously.",
        "If you are in crisis, call or text 988. The 988 Suicide and Crisis Lifeline is available 24/7, free and confidential.",
      ],
    },
    {
      heading: "SAINT® TMS Side Effects: What the Trial Data Shows",
      table: {
        headers: [
          "Side effect",
          "How common in SAINT® trials",
          "Typical duration",
          "What we do about it",
        ],
        rows: [
          [
            "Headache",
            "Most common adverse event in the 2022 trial (Cole et al.)",
            "Hours to 1–2 days; decreases over the week",
            "Over-the-counter pain relievers; adjust intensity if needed",
          ],
          [
            "Scalp discomfort",
            "Common; mild to moderate",
            "Decreases over first 1–2 days",
            "Adjust coil position or reduce intensity",
          ],
          [
            "Facial twitching",
            "Common during pulses; harmless",
            "Stops when stimulation stops",
            "No action needed; expected and temporary",
          ],
          [
            "Fatigue",
            "Reported by some participants",
            "Typically during the treatment week",
            "Rest between sessions; usually resolves",
          ],
          [
            "Anxiety",
            "Reported by some participants",
            "Variable",
            "Discuss with the treatment team; adjust as needed",
          ],
          [
            "Seizure",
            "Rare — estimated <0.1% with standard TMS parameters [VERIFY — rate for accelerated protocols]",
            "Acute event",
            "Screening excludes high-risk patients; stop treatment if it occurs",
          ],
          [
            "Worsening depression / suicidal thoughts",
            "Possible with any depression treatment; monitored in trials",
            "Variable",
            "Report immediately; team adjusts or pauses treatment; 988 for crisis",
          ],
        ],
      },
    },
    {
      heading: "What SAINT® Does Not Cause",
      paragraphs: [
        "SAINT® does not cause the memory problems that can follow ECT (electroconvulsive therapy). It does not use anesthesia or induce a seizure. It does not cause the weight gain, sexual dysfunction, nausea, or insomnia that are common side effects of many antidepressant medications.",
        "SAINT® does not require any recovery time after sessions — patients can drive and resume normal activities. It does not involve any chemical entering the body. These differences are part of why some people who haven't tolerated medication side effects consider SAINT®.",
        "[[Compare SAINT® to other treatments|/learn/saint-vs-traditional-tms]] or [[see what SAINT® feels like|/learn/what-does-saint-tms-feel-like]].",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "Blumberger DM, et al. Effectiveness of theta burst versus high-frequency repetitive transcranial magnetic stimulation in major depression (THREE-D trial). Lancet. 2018;391(10131):1683-1692.",
      url: "https://doi.org/10.1016/S0140-6736(18)30295-2",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: safetyRelated,
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "October 2026",
    lastReviewed: "October 2026",
    lastUpdated: "October 2026",
  },
  toc: [
    {
      label: "Side Effects: Quick Answer",
      href: "#saint-tms-side-effects-quick-answer",
    },
    { label: "Quick Answer", href: "#quick-answer-is-saint-tms-safe" },
    { label: "Common Side Effects", href: "#common-side-effects-of-saint-tms" },
    {
      label: "Rare but Serious Risks",
      href: "#rare-but-serious-risks-seizure-mania-and-worsening-depression",
    },
    {
      label: "Long-Term Effects",
      href: "#long-term-effects-what-the-evidence-does-and-doesnt-show",
    },
    {
      label: "Does TMS Damage the Brain?",
      href: "#does-tms-damage-the-brain-myth-vs-data",
    },
    {
      label: "Who Should Not Have SAINT®",
      href: "#who-should-not-have-saint-contraindications",
    },
    {
      label: "How the Clinic Monitors You",
      href: "#how-the-clinic-monitors-you-during-the-saint-week",
    },
    {
      label: "Side Effects Table",
      href: "#saint-tms-side-effects-what-the-trial-data-shows",
    },
    {
      label: "What SAINT® Does Not Cause",
      href: "#what-saint-does-not-cause",
    },
    { label: "FAQ", href: "#faq" },
    { label: "Cited Research", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford University.",
  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "Learn If It's a Fit",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Check my insurance",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});
